import React from 'react'
import styled from '@emotion/styled'
import { Button, ConfigProvider } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import { PortalSectionSaveControlProps } from '../helpers/editor-section-save'

export const portalEditorColors = {
  navy: '#122A41',
  orange: '#FF6422',
  orangeHover: '#E95718',
  red: '#EA4335',
  border: '#E2E8F0',
  group: '#F4F4F4',
  surface: '#FFFFFF',
  hover: '#F9FAFB',
  muted: '#6B7A8C',
} as const

export function PortalEditorTheme({ children }: { children: React.ReactNode }) {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: portalEditorColors.orange,
          colorError: portalEditorColors.red,
          colorText: portalEditorColors.navy,
          colorBorder: portalEditorColors.border,
          colorBgContainer: portalEditorColors.surface,
          borderRadius: 6,
          controlHeight: 40,
        },
      }}
    >
      <ThemeScope>{children}</ThemeScope>
    </ConfigProvider>
  )
}

export function PortalSectionSaveButton({
  disabled,
  dirty,
  loading,
  error,
  onSave,
}: PortalSectionSaveControlProps) {
  return (
    <SaveArea>
      <SaveButton
        type="primary"
        loading={loading}
        disabled={disabled || !dirty || loading}
        icon={!dirty && !disabled && !loading ? <CheckOutlined /> : undefined}
        onClick={onSave}
      >
        {loading ? 'Salvando…' : dirty ? 'Salvar conteúdo' : 'Salvo'}
      </SaveButton>
      {error ? <SaveError role="alert">{error}</SaveError> : null}
    </SaveArea>
  )
}

const ThemeScope = styled.div`
  min-height: 100%;
  color: ${portalEditorColors.navy};

  .ant-btn {
    box-shadow: none;
    font-weight: 600;
  }

  .ant-btn-primary {
    color: ${portalEditorColors.navy};
  }

  .ant-btn-primary:not(:disabled):hover,
  .ant-btn-primary:not(:disabled):focus {
    border-color: ${portalEditorColors.orangeHover};
    background: ${portalEditorColors.orangeHover};
    color: ${portalEditorColors.navy};
  }

  .ant-btn-default {
    border-color: ${portalEditorColors.border};
    color: ${portalEditorColors.navy};
  }

  .ant-btn-default:not(:disabled):hover {
    border-color: ${portalEditorColors.orange};
    color: ${portalEditorColors.orange};
  }

  .ant-btn-sm {
    font-size: 12px;
  }

  .ant-btn-dangerous,
  .ant-btn-dangerous:not(:disabled):hover {
    border-color: ${portalEditorColors.red};
    color: ${portalEditorColors.red};
  }

  .ant-input,
  .ant-input-number,
  .ant-picker,
  .ant-select-selector {
    border-color: ${portalEditorColors.border} !important;
  }

  .ant-input:hover,
  .ant-input:focus,
  .ant-input-number:hover,
  .ant-input-number-focused,
  .ant-picker:hover,
  .ant-picker-focused,
  .ant-select-focused .ant-select-selector {
    border-color: ${portalEditorColors.orange} !important;
    box-shadow: 0 0 0 2px rgba(255, 100, 34, 0.12) !important;
  }

  .ant-card {
    box-shadow: none;
  }

  .ant-card-small > .ant-card-head {
    min-height: 48px;
    border-bottom-color: ${portalEditorColors.border};
    padding: 0 20px;
  }

  .ant-card-small > .ant-card-head .ant-card-head-title {
    padding: 14px 0;
    color: ${portalEditorColors.navy};
    font-weight: 700;
  }

  .ant-card-small > .ant-card-body {
    padding: 20px;
  }

  details {
    box-sizing: border-box;
    border: 1px solid ${portalEditorColors.border};
    border-radius: 6px;
    padding: 12px;
    background: ${portalEditorColors.hover};
  }

  details > summary {
    color: ${portalEditorColors.navy} !important;
    font-size: 12px;
    font-weight: 600;
  }
`

const SaveArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  justify-content: flex-start;
  width: 100%;
  padding-top: 8px;
`

const SaveButton = styled(Button)`
  min-width: 144px;
`

const SaveError = styled.span`
  color: ${portalEditorColors.red};
  font-size: 12px;
`
